import { useCallback, useEffect, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useSearchContentQuery } from 'services/resourceService';
import { ContentSearchQueryData } from '../types';
import { CONTENT_SEARCH_PAGE_SIZE } from '../utils/contentSearchUtils';

const EMPTY_RESULTS: ContentSearchQueryData = {
  care_programs: [],
  on_demand: [],
  careProgramsHasMore: false,
  onDemandHasMore: false,
};

export interface UseContentSearchResult {
  contentResults: ContentSearchQueryData;
  nextPageIndex: number;
  careProgramsHasMore: boolean;
  onDemandHasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  loadNextPage: () => void;
}

/** Resets to page 0 when searchTerm changes. */
export const useContentSearch = (searchTerm: string): UseContentSearchResult => {
  const [nextPageIndex, setNextPageIndex] = useState(0);

  useEffect(() => {
    setNextPageIndex(0);
  }, [searchTerm]);

  const { data, isLoading, isFetching, error } = useSearchContentQuery(
    { search: searchTerm, page: nextPageIndex, pageSize: CONTENT_SEARCH_PAGE_SIZE },
    { skip: !searchTerm },
  );

  const contentResults = data ?? EMPTY_RESULTS;
  const moreResultsAvailable = contentResults.careProgramsHasMore || contentResults.onDemandHasMore;

  const loadNextPage = useCallback(() => {
    if (moreResultsAvailable && !isFetching) {
      setNextPageIndex((previousPageIndex) => previousPageIndex + 1);
    }
  }, [moreResultsAvailable, isFetching]);

  return {
    contentResults,
    nextPageIndex,
    careProgramsHasMore: contentResults.careProgramsHasMore,
    onDemandHasMore: contentResults.onDemandHasMore,
    isLoading,
    isFetching,
    error,
    loadNextPage,
  };
};
